import React from "react"
import styled from "@emotion/styled"
import { useTranslation } from "react-i18next"
import { ToggleGroup } from "@base-ui-components/react/toggle-group"
import { Toggle } from "@base-ui-components/react/toggle"

import {
  THREAD_MODE_LIVE_CHAT,
  SEGMENT_ACTIVE,
  SEGMENT_AVAILABLE,
  SEGMENT_CHANNEL_DISABLED,
  SEGMENT_UNREACHABLE,
  TRANSITION_TOGGLE_LIVE_CHAT,
  TRANSITION_DISPATCH,
  threadModeSegments,
} from "../../threadMode"
import { declaredIcon } from "../../declaredIcons"

// PLAT-3418 - one segmented toggle, N segments.
//
// Base UI's ToggleGroup is headless (Roy locked Base UI in PLAT-3417), so styling
// stays styled-components. Tooltips stay on react-tooltip because the header
// already uses it - two tooltip systems in one header is worse than one.

const ACTIVE_BG = "#405DE6"
// Matches the header's other icon buttons, measured: they are 34x34 with
// `1px solid rgb(204,204,204)` and radius 4px.
const BORDER = "#cccccc"
const TEXT = "#707070"
const DISABLED_TEXT = "#b3b3b3"
const DISABLED_BG = "#f2f2f2"

const Group = styled(ToggleGroup)`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${BORDER};
  /* 4px, matching the header's other icon buttons - measured, see BORDER. */
  border-radius: 4px;
  overflow: hidden;
  margin-right: 8px;
  /* The header is a flex row that can get tight; the toggle must not be the
     thing that overflows it. */
  max-width: 100%;
`

/**
 * One segment. `data-state` rather than a boolean per variant, so the four
 * states are mutually exclusive by construction.
 */
const Segment = styled(Toggle)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 32px of content inside the group's own 1px border gives a 34px group, flush
     with the header's other 34x34 icon buttons. Fixed rather than hugging the
     glyph so every segment is the same width. */
  padding: 8px;
  min-height: 32px;
  min-width: 32px;
  border: none;
  /* THE SEPARATOR, drawn per segment rather than as a divider element: a
     non-Toggle child would land in Base UI's composite list, which its roving
     tabindex walks. */
  border-left: 1px solid ${BORDER};
  background: #ffffff;
  color: ${TEXT};
  font-size: 0.8125rem;
  cursor: pointer;

  &:first-of-type {
    border-left: none;
  }

  &[data-state="${SEGMENT_ACTIVE}"] {
    background: ${ACTIVE_BG};
    color: #ffffff;
    cursor: default;
  }

  /* CHANNEL-DISABLED: greyed rather than hidden, so the agent learns the
     capability exists and is off here. Distinct from active and in-flight. */
  &[data-state="${SEGMENT_CHANNEL_DISABLED}"] {
    background: ${DISABLED_BG};
    color: ${DISABLED_TEXT};
    cursor: not-allowed;
  }

  /* UNREACHABLE FROM HERE: a property of the current mode, not the channel.
     Same non-actionable treatment, different tooltip. */
  &[data-state="${SEGMENT_UNREACHABLE}"] {
    background: #ffffff;
    color: ${DISABLED_TEXT};
    cursor: not-allowed;
  }

  /* In-flight is orthogonal to all four states, so it is its own attribute
     rather than a fifth state. */
  &[data-in-flight="true"] {
    opacity: 0.6;
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid ${ACTIVE_BG};
    outline-offset: -2px;
  }
`

/**
 * MUST stay at or above 16px. The declared sparkle is an outline glyph with
 * stroke-width 1.5 in a 24-unit viewBox, so its effective stroke is
 * `1.5 * rendered / 24` - at the segment's own 13px font-size that was 0.813px,
 * sub-pixel, and visibly fainter than the solid glyphs beside it.
 *
 * `rem`, not `px`: the segment's font-size is already rem, so the two scale
 * together under browser zoom.
 */
const SEGMENT_ICON_SIZE = "1rem"

// Keeps the icon from being squashed by the flex row.
const SegmentIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
`

/**
 * The reason a segment is unavailable, for assistive tech only - the visible
 * reason is a react-tooltip, which a screen reader never sees.
 *
 * MUST NOT be `display: none`: that removes it from the accessibility tree and
 * `aria-describedby` then resolves to nothing.
 */
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

function ThreadModeToggle({
  thread,
  threadMode,
  isInFlightLiveChat,
  isInFlightThreadModeUpdate,
  // One boolean the caller computes - only it knows about
  // superadmin/superauditor/assign/move. Separate from segment state because it
  // blocks every segment for a reason unrelated to modes.
  roleBlocked,
  onToggleLiveChat,
  onRequestThreadMode,
}) {
  const { t } = useTranslation("common")

  const segments = threadModeSegments({ thread, threadMode })

  const builtInLabel = (key) => (key === THREAD_MODE_LIVE_CHAT
    ? t?.("live_chat.live_chat_button")
    : t?.("live_chat.bot_button"))

  const builtInIconName = (key) => (key === THREAD_MODE_LIVE_CHAT ? "headset" : "robot")

  const tooltipFor = (segment, label) => {
    // EVERY state returns a tooltip, including the active one: the segments are
    // icons only, so this is the only way a sighted user learns what one is.
    if (segment.state === SEGMENT_ACTIVE) {
      return t?.("live_chat.tooltip_current_mode", { label })
    }
    if (segment.state === SEGMENT_CHANNEL_DISABLED) {
      return t?.("live_chat.tooltip_mode_channel_disabled", { label })
    }
    if (segment.state === SEGMENT_UNREACHABLE) {
      // The one string not derived from a declared label - the reason is inbox's
      // own transition rule, not the extension's.
      return t?.("live_chat.tooltip_bot_unreachable_from_mode")
    }
    return t?.("live_chat.tooltip_activate_mode", { label })
  }

  const group = (
    <Group
      // Base UI wants the value as an array; a thread is in exactly one mode.
      value={[threadMode]}
      // NO onValueChange: Base UI reports the new value AFTER toggling its own
      // state, which would move the segment before the server agreed. Each
      // segment handles its own click; the value comes back from `threadMode`.
      //
      // The group MUST NOT reuse a button's label - naming it `live_chat_button`
      // gave `group "Live Chat"` containing `button "Live Chat"`.
      aria-label={t?.("live_chat.thread_mode_group")}
    >
      {segments.map((segment) => {
        const label = segment.isBuiltIn ? builtInLabel(segment.key) : segment.label
        const iconName = segment.isBuiltIn ? builtInIconName(segment.key) : segment.iconName
        const inFlight = segment.transition.kind === TRANSITION_DISPATCH
          ? !!isInFlightThreadModeUpdate
          : !!isInFlightLiveChat
        const actionable = segment.state === SEGMENT_AVAILABLE && !roleBlocked && !inFlight
        // The reason a segment cannot be used, as an accessible description.
        // Only rendered when there IS a reason worth announcing - the active
        // segment is self-evident from aria-pressed and needs no prose.
        const reason = (segment.state === SEGMENT_CHANNEL_DISABLED
          || segment.state === SEGMENT_UNREACHABLE)
          ? tooltipFor(segment, label)
          : null
        const reasonId = reason ? `thread-mode-reason-${segment.key}` : null

        return (
          <Segment
            key={segment.key}
            value={segment.key}
            data-state={segment.state}
            data-in-flight={inFlight ? "true" : "false"}
            data-mode-key={segment.key}
            // NEITHER `disabled` NOR `aria-disabled`. `disabled` drops the button
            // from the tab order, and Base UI's roving tabindex SKIPS
            // `aria-disabled` items - which made a channel-disabled segment
            // completely unreachable by keyboard. `disabledIndices={[]}` does not
            // help; ToggleGroup does not forward it. Unavailability is carried by
            // the accessible description instead, so the agent can still reach
            // the segment and find out why.
            //
            // `aria-label` IS THE ONLY ACCESSIBLE NAME. The segments are icons
            // only and the svg is aria-hidden, so without this a screen reader
            // announces "button". A tooltip does not substitute: react-tooltip
            // renders elsewhere in the DOM and is not referenced by the button.
            //
            // Name and description MUST stay separate - rendering the reason
            // inside the button concatenated it into the name, as
            // "Meta AIMeta AI is disabled for this channel".
            aria-label={label}
            aria-describedby={reasonId || undefined}
            data-tooltip-content={tooltipFor(segment, label)}
            data-tooltip-id="chat-section-tooltip"
            onClick={(event) => {
              if (!actionable) {
                event.preventDefault()
                return
              }
              const { kind, target } = segment.transition
              if (kind === TRANSITION_TOGGLE_LIVE_CHAT) {
                onToggleLiveChat(target)
                return
              }
              if (kind === TRANSITION_DISPATCH) {
                onRequestThreadMode(target)
              }
            }}
          >
            {/* Icons only - Roy's design, 2026-09-01 GMT+8. There is no visible
                label; the tooltip and `aria-label` above are how it is conveyed.

                THE SIZE MUST GO THROUGH THE `size` PROP, NOT CSS on SegmentIcon.
                `fromFontAwesome` writes `fontSize` as an INLINE STYLE, which
                beats a styled-components class - a CSS font-size here would
                resize the outline icon and leave the FontAwesome ones at 13px. */}
            <SegmentIcon as={declaredIcon(iconName)} size={SEGMENT_ICON_SIZE} />
          </Segment>
        )
      })}
    </Group>
  )

  return (
    <>
      {group}
      {/*
        THE REASONS MUST LIVE OUTSIDE THE BUTTONS. Inside a <Segment> the hidden
        text is concatenated into the button's accessible NAME, and outside the
        Group also keeps Base UI's composite list seeing only Toggles as
        children, so segment indices stay correct.
      */}
      {segments.map((segment) => {
        const label = segment.isBuiltIn ? builtInLabel(segment.key) : segment.label
        const reason = (segment.state === SEGMENT_CHANNEL_DISABLED
          || segment.state === SEGMENT_UNREACHABLE)
          ? tooltipFor(segment, label)
          : null
        if (!reason) {
          return null
        }
        return (
          <VisuallyHidden key={`reason-${segment.key}`} id={`thread-mode-reason-${segment.key}`}>
            {reason}
          </VisuallyHidden>
        )
      })}
    </>
  )
}

export default ThreadModeToggle
